import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  Toolbar,
  useGridApiContext,
} from "@mui/x-data-grid";

// Labelled text buttons rather than the v9 default toolbar's icon-only ones, to
// match the Admin Portal's lists: COLUMNS / FILTERS on the left, print and
// download on the right.
const TOOLBAR_BUTTON_SX = {
  textTransform: "uppercase",
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "text.primary",
  "& .MuiButton-startIcon": { color: "text.secondary" },
} as const;

const TOOLBAR_ICON_SX = { color: "text.secondary" } as const;

// Toolbar itself renders a bare div and takes no `sx`, so the strip's styling
// comes from a Box handed to its `render` prop — which keeps Toolbar's keyboard
// navigation and ARIA roles while letting the Box carry the theme-aware colors.
const TOOLBAR_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.5,
  bgcolor: "action.hover",
  borderBottom: 1,
  borderColor: "divider",
} as const;

// Extra props reaching the toolbar through the grid's `slotProps.toolbar`.
declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    jurisdiction?: string;
  }
}

export interface VerificationsGridToolbarProps {
  /** Jurisdiction the list is scoped to, named in the export file. */
  jurisdiction?: string;
}

/**
 * Downloads land as
 * `act-kyc-console-verifications-list_Rio-De-Janeiro_20260908_1432.csv` — the
 * Admin Portal's `<name>_YYYYMMDD_HHmm` convention (see its DateUtils'
 * getCurrentDateYYYYMMDDWithTime), with the selected jurisdiction in between so
 * exports taken from different tabs don't overwrite each other in the downloads
 * folder. Jurisdiction names arrive from the API and can carry spaces, so
 * anything outside [A-Za-z0-9] collapses to a single hyphen. The grid appends
 * the `.csv` extension itself.
 */
function getExportFileName(jurisdiction?: string): string {
  const scope = jurisdiction
    ?.trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return [
    "act-kyc-console-verifications-list",
    ...(scope ? [scope] : []),
    dayjs().format("YYYYMMDD_HHmm"),
  ].join("_");
}

/** The four keys used below all resolve to plain strings in MUI's locale bundles. */
type ToolbarLabelKey =
  "toolbarColumns" | "toolbarFilters" | "toolbarExportPrint" | "toolbarExportCSV";

function VerificationsGridToolbar({ jurisdiction }: VerificationsGridToolbarProps) {
  const apiRef = useGridApiContext();
  // Wording comes from the grid's own locale bundle (wired up in
  // AppThemedContent), so these labels translate with the rest of the grid
  // chrome instead of needing their own keys in the app's locale files.
  const label = (key: ToolbarLabelKey) => apiRef.current.getLocaleText(key) as string;

  return (
    <Toolbar render={<Box sx={TOOLBAR_SX} />}>
      <ColumnsPanelTrigger
        render={<Button size="small" startIcon={<ViewColumnIcon />} sx={TOOLBAR_BUTTON_SX} />}
      >
        {label("toolbarColumns")}
      </ColumnsPanelTrigger>
      <FilterPanelTrigger
        render={<Button size="small" startIcon={<FilterListIcon />} sx={TOOLBAR_BUTTON_SX} />}
      >
        {label("toolbarFilters")}
      </FilterPanelTrigger>

      {/* Pushes the export controls to the right-hand end of the strip. */}
      <Box sx={{ flexGrow: 1 }} />

      <Tooltip title={label("toolbarExportPrint")}>
        <IconButton
          size="small"
          aria-label={label("toolbarExportPrint")}
          onClick={() =>
            apiRef.current.exportDataAsPrint({ fileName: getExportFileName(jurisdiction) })
          }
          sx={TOOLBAR_ICON_SX}
        >
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={label("toolbarExportCSV")}>
        <IconButton
          size="small"
          aria-label={label("toolbarExportCSV")}
          onClick={() =>
            apiRef.current.exportDataAsCsv({ fileName: getExportFileName(jurisdiction) })
          }
          sx={TOOLBAR_ICON_SX}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Toolbar>
  );
}

export default VerificationsGridToolbar;
